// "App verbinden" (?view=connect&request=…): the consent step of an OAuth sign-in (server
// oauth.rs). An AI app (claude.ai, Claude Code, Cursor …) asked to act for the signed-in person; the
// page names the app and where the answer goes, and sends the person's choice to the server, which
// answers with the address to return to (a one-time code, or "access_denied").
import { api, ApiError, type Session } from "./account";

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

interface Request { client: string; from?: string | null; target: string; local: boolean; app?: boolean; user: { name: string; short: string } }

/** Schemes that run code or reach files in the browser (same list as the server's BAD_SCHEMES). */
const BAD_SCHEMES = ["javascript", "data", "vbscript", "file", "blob", "about", "filesystem", "ws", "wss", "ftp", "mailto", "tel", "sms", "view-source", "chrome", "chrome-extension", "moz-extension", "intent", "jar", "resource"];

/** Own schemes of AI apps and editors without a dot (same list as the server's APP_SCHEMES). */
const APP_SCHEMES = ["cursor", "vscode", "vscode-insiders", "vscodium", "windsurf", "zed", "claude", "kiro"];

/** Only addresses the server allows for a return: https, http on this computer, an app's own
 *  scheme (cursor://…, com.example.app:/…) – checked again here before the page goes there. */
export function safeReturn(uri: string): boolean {
  try {
    const u = new URL(uri);
    if (u.username || u.password) return false;
    if (u.protocol === "https:") return true;
    if (u.protocol === "http:") return ["localhost", "127.0.0.1", "[::1]"].includes(u.hostname);
    const scheme = u.protocol.slice(0, -1);
    return /^[a-z][a-z0-9+.-]*$/.test(scheme) && !BAD_SCHEMES.includes(scheme) && (scheme.includes(".") || APP_SCHEMES.includes(scheme));
  } catch {
    return false;
  }
}

export class Connect {
  private root = document.getElementById("lw-connect") as HTMLElement;
  private readonly id = new URLSearchParams(location.search).get("request") ?? "";

  constructor(private session: Session) {
    document.title = "App verbinden – Lanewerk";
    void this.load();
  }

  private show(...parts: HTMLElement[]) {
    const card = el("section", "card-page connect");
    card.append(...parts);
    this.root.replaceChildren(card);
  }

  private gone(text = "Diese Anfrage ist abgelaufen oder wurde schon beantwortet. Starte die Verbindung in der App noch einmal.") {
    const p = el("p", "card-error", text);
    p.setAttribute("role", "alert");
    this.show(el("h1", undefined, "App verbinden"), p);
  }

  private async load() {
    if (!this.session.auth) return this.gone("Lanewerk läuft ohne Konten – Apps verbinden sich hier ohne Anmeldung (siehe KI-Zugang).");
    let r: Request;
    try {
      r = await api<Request>(`./api/oauth/requests/${encodeURIComponent(this.id)}`);
    } catch (e) {
      return this.gone(e instanceof ApiError && e.status === 404 ? undefined : `Die Anfrage konnte nicht geladen werden (${(e as Error).message}).`);
    }
    const lead = el("p", "connect-lead");
    lead.append(el("strong", undefined, r.client), " möchte sich mit Lanewerk verbinden und in deinem Namen arbeiten.");
    // who gave the name: the host that publishes the app's details (client metadata document) or
    // the app itself at its registration – nobody checked it either way
    const origin = el("p", "menu-hint", r.from
      ? `Name und Adressen der App kommen von ${r.from} – erlaube nur, wenn du diese Adresse kennst.`
      : "Den Namen hat die App selbst angegeben.");
    const what = el("ul", "connect-list");
    for (const t of [
      "deine Boards, Karten und Aufgaben lesen",
      "Karten anlegen, ändern und verschieben, Abhängigkeiten setzen – mit deinen Rechten auf jedem Board",
      "nichts löschen; jede Änderung steht im Verlauf der Karte mit „(KI)“",
    ]) what.appendChild(el("li", undefined, t));
    const who = el("p", "menu-hint", `Angemeldet als ${r.user.name} (${r.user.short}). Danach geht es zurück zu ${r.local ? `einem Programm auf diesem Rechner (${r.target})` : r.app ? `der App auf diesem Rechner (${r.target})` : r.target}.`);
    const warn = el("p", "connect-warn", "Erlaube das nur, wenn du die Verbindung gerade selbst gestartet hast. Trennen kannst du die App jederzeit unter KI-Zugang.");
    const row = el("div", "connect-actions");
    const yes = el("button", "primary", "Erlauben");
    yes.type = "button";
    yes.id = "lw-connect-allow";
    const no = el("button", undefined, "Ablehnen");
    no.type = "button";
    no.id = "lw-connect-deny";
    const status = el("p", "home-status");
    status.setAttribute("role", "status");
    yes.addEventListener("click", () => void this.answer(true, [yes, no], status));
    no.addEventListener("click", () => void this.answer(false, [yes, no], status));
    row.append(yes, no);
    this.show(el("h1", undefined, "App verbinden"), lead, origin, what, who, warn, row, status);
    yes.focus();
  }

  private async answer(allow: boolean, buttons: HTMLButtonElement[], status: HTMLElement) {
    for (const b of buttons) b.disabled = true;
    try {
      const r = await api<{ redirect: string }>(`./api/oauth/requests/${encodeURIComponent(this.id)}`, "POST", { allow });
      if (!safeReturn(r.redirect)) throw new Error("unerwartete Rücksprung-Adresse");
      status.textContent = allow ? "Verbunden – du wirst zur App zurückgeleitet. Danach kannst du dieses Fenster schliessen." : "Abgelehnt – die App bekommt keinen Zugang.";
      location.href = r.redirect;
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return this.gone();
      status.textContent = `Das hat nicht geklappt (${(e as Error).message}).`;
      for (const b of buttons) b.disabled = false;
    }
  }
}
